import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Empresa } from '../../../models/empresa.model';
import {
  SunatReconsultaError,
  SunatSyncSummary,
} from '../../../models/sunat.model';
import { ModalRef } from '../../../services/modal.service';
import { AuthService } from '../../../services/auth.service';
import {
  SUNAT_RECONSULTA_LIMIT_DEFAULT,
  SUNAT_RECONSULTA_LIMIT_MAX,
  SUNAT_RECONSULTA_LIMIT_MIN,
  SunatService,
} from '../../../services/sunat.service';

@Component({
  selector: 'app-modal-sunat',
  imports: [FormsModule],
  templateUrl: './modal-sunat.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './modal-sunat.component.css',
})
export class ModalSunatComponent implements OnInit, OnDestroy {
  private readonly sunatService = inject(SunatService);
  private readonly authService = inject(AuthService);
  private readonly destroy$ = new Subject<void>();

  modal = input.required<ModalRef>();
  empresa = input.required<Empresa | null>();
  empresas = input<Empresa[]>([]);

  reconsultaExitosa = output<SunatSyncSummary>();
  reconsultaError = output<SunatReconsultaError>();

  readonly limitMin = SUNAT_RECONSULTA_LIMIT_MIN;
  readonly limitMax = SUNAT_RECONSULTA_LIMIT_MAX;

  empresaId: number | null = null;
  limit = SUNAT_RECONSULTA_LIMIT_DEFAULT;
  confirmando = false;
  ejecutando = false;
  resumen: SunatSyncSummary | null = null;
  error: SunatReconsultaError | null = null;

  ngOnInit(): void {
    this.empresaId = this.empresa()?.id ?? null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get empresaActual(): Empresa | null {
    const id = this.empresaId;
    const fromList = this.empresas().find((item) => item.id === id);

    if (fromList) {
      return fromList;
    }

    return this.empresa()?.id === id ? this.empresa() : null;
  }

  onEmpresaChange(): void {
    if (this.ejecutando) {
      return;
    }

    this.resumen = null;
    this.error = null;
    this.confirmando = false;
  }

  get ejecutadoPor(): string {
    return this.authService.getCurrentUser()?.email ?? 'usuario actual';
  }

  get limitInvalido(): boolean {
    const value = Number(this.limit);
    return (
      !Number.isInteger(value) ||
      value < SUNAT_RECONSULTA_LIMIT_MIN ||
      value > SUNAT_RECONSULTA_LIMIT_MAX
    );
  }

  get bloqueoPresunto(): boolean {
    return (
      !!this.resumen &&
      this.resumen.en_curso !== true &&
      this.sunatService.esResumenEnCeros(this.resumen)
    );
  }

  get empresaSinToken(): boolean {
    return !this.empresaActual?.token_sunat?.trim();
  }

  get duracionSegundos(): string {
    if (!this.resumen) {
      return '0.0';
    }
    return (this.resumen.duracion_ms / 1000).toFixed(1);
  }

  solicitarConfirmacion(): void {
    if (this.ejecutando) {
      return;
    }

    this.error = null;

    if (this.limitInvalido) {
      this.error = {
        success: false,
        message: `El límite debe ser un número entero entre ${SUNAT_RECONSULTA_LIMIT_MIN} y ${SUNAT_RECONSULTA_LIMIT_MAX}.`,
        status: 0,
        details: { limit: this.limit },
      };
      return;
    }

    this.confirmando = true;
  }

  cancelarConfirmacion(): void {
    if (!this.ejecutando) {
      this.confirmando = false;
    }
  }

  ejecutarReconsulta(): void {
    // Guard explícito contra doble envío, además del [disabled] del botón.
    if (this.ejecutando) {
      return;
    }

    const empresa = this.empresaActual;

    if (!empresa) {
      this.error = {
        success: false,
        message: 'No se encontró la empresa seleccionada.',
        status: 0,
        details: null,
      };
      return;
    }

    if (this.limitInvalido) {
      this.confirmando = false;
      this.solicitarConfirmacion();
      return;
    }

    this.confirmando = false;
    this.ejecutando = true;
    this.error = null;
    this.resumen = null;

    this.sunatService
      .reconsultarVentasPendientes(empresa.id, Number(this.limit))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resumen) => {
          this.resumen = resumen;
          this.ejecutando = false;
          this.reconsultaExitosa.emit(resumen);
        },
        error: (error: unknown) => {
          const normalized = this.toReconsultaError(error);
          this.error = normalized;
          this.ejecutando = false;
          this.reconsultaError.emit(normalized);
        },
      });
  }

  cerrar(): void {
    if (this.ejecutando) {
      return;
    }
    this.modal().close();
  }

  private toReconsultaError(value: unknown): SunatReconsultaError {
    const candidate = value as Partial<SunatReconsultaError> | null;

    if (candidate && typeof candidate.message === 'string') {
      return {
        success: false,
        message: candidate.message,
        status: typeof candidate.status === 'number' ? candidate.status : 0,
        details: candidate.details ?? null,
      };
    }

    return {
      success: false,
      message: 'No se pudo reconsultar el estado SUNAT de las ventas pendientes.',
      status: 0,
      details: value,
    };
  }
}
